// components/AboutAttractionSection.client.tsx
"use client";

import * as React from "react";
import clsx from "clsx";
import Image from "next/image";
import { LazyMotion, domAnimation, m, type MotionProps } from "framer-motion";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@relume_io/relume-ui";
import { RxChevronDown } from "react-icons/rx";
import CtaButton from "@/components/ui/CtaButton";

export type MoreInfoItem = {
    title: string;
    description: string;
};

type BaseProps = {
    heading: string;
    description: string;
    features: string[];
    moreInfoItems: MoreInfoItem[];
};

type ConflictingHtmlEvents =
    | "onAnimationStart"
    | "onAnimationEnd"
    | "onAnimationIteration"
    | "onDrag"
    | "onDragStart"
    | "onDragEnd"
    | "onDragEnter"
    | "onDragLeave"
    | "onDragOver"
    | "onDrop";

type SafeHeaderHtmlProps = Omit<
    React.ComponentPropsWithoutRef<"header">,
    keyof BaseProps | keyof MotionProps | ConflictingHtmlEvents
>;

export type AboutAttractionSectionClientProps = BaseProps & SafeHeaderHtmlProps & MotionProps;

const slideUp = {
    hidden: { y: 20, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: {
            duration: 0.7,
            ease: [0.215, 0.61, 0.355, 1],
            delay: i * 0.1,
        },
    }),
} as const;

function FeatureRow({ feature }: { feature: string }) {
    return (
        <div className="flex items-start">
            <div className="mr-4 flex h-10 w-10 flex-shrink-0 items-center justify-center">
                <Image src="/check.svg" alt="" width={24} height={24} className="h-6 w-6" />
            </div>
            <p>{feature}</p>
        </div>
    );
}

export default function AboutAttractionSectionClient(props: AboutAttractionSectionClientProps) {
    const { heading, description, features, moreInfoItems, className, ...rest } = props;

    return (
        <LazyMotion features={domAnimation}>
            <m.header
                id="relume"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className={clsx("px-[5%] py-12 md:py-16 lg:py-20", className)}
                {...rest}
            >
                <div className="container">
                    <div className="grid grid-cols-1 gap-y-12 md:gap-y-16 lg:grid-cols-[1.25fr_0.5fr] lg:gap-x-20">
                        {/* LEWA KOLUMNA */}
                        <div>
                            <m.h2
                                variants={slideUp}
                                custom={0}
                                className="mb-3 max-w-[44rem] text-4xl font-medium md:mb-4 md:text-5xl will-change-transform"
                            >
                                {heading}
                            </m.h2>

                            <m.p variants={slideUp} custom={1} className="max-w-lg md:text-md will-change-transform">
                                {description}
                            </m.p>

                            <m.div
                                variants={slideUp}
                                custom={2}
                                className="my-8 rounded-3xl border bg-secondary px-4 py-8 text-primary md:px-8"
                            >
                                <p className="mb-3 font-medium uppercase md:mb-8 md:text-md">Efekt dla zespołu:</p>

                                <div className="grid grid-cols-1 gap-y-6 md:grid-cols-2 md:gap-x-8">
                                    {features.map((feature, index) => (
                                        <FeatureRow key={`${feature}-${index}`} feature={feature} />
                                    ))}
                                </div>
                            </m.div>

                            <m.h2
                                variants={slideUp}
                                custom={3}
                                className="mb-3 text-xl font-medium uppercase md:mb-4 md:text-2xl will-change-transform"
                            >
                                Szczegóły atrakcji integracyjnej dla firm
                            </m.h2>

                            <m.div variants={slideUp} custom={4} className="will-change-transform">
                                <Accordion type="multiple">
                                    {moreInfoItems.map((item, index) => (
                                        <AccordionItem
                                            className="hover:text-accent transition-colors duration-300"
                                            key={`${item.title}-${index}`}
                                            value={`item-${index}`}
                                        >
                                            <AccordionTrigger
                                                className="justify-start py-4 font-medium md:py-5 md:text-md"
                                                icon={
                                                    <RxChevronDown className="order-first mr-4 h-6 w-[1.75rem] shrink-0 text-black transition-transform duration-300 md:size-6" />
                                                }
                                            >
                                                <h3 className="text-left text-base font-medium leading-snug md:text-md">{item.title}</h3>
                                            </AccordionTrigger>

                                            <AccordionContent className="md:pb-6 text-black">
                                                <p className="text-sm leading-relaxed md:text-base">{item.description}</p>
                                            </AccordionContent>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            </m.div>
                        </div>

                        {/* PRAWA KOLUMNA (Sticky Box) */}
                        <div>
                            <m.div variants={slideUp} custom={5} className="lg:sticky lg:top-32 will-change-transform">
                                <div className="mb-5 rounded-3xl bg-secondary p-6 text-start text-primary md:mb-6 md:p-8">
                                    <h2 className="mb-2 text-xl font-medium md:text-2xl">Masz więcej pytań ?</h2>
                                    <p className="mb-4 text-sm md:text-base">
                                        Skontaktuj się z nami, a my pomożemy Ci we wszystkim!
                                    </p>
                                    <CtaButton />
                                </div>
                            </m.div>
                        </div>
                    </div>
                </div>
            </m.header>
        </LazyMotion>
    );
}